import { ref, computed, onMounted } from 'vue'
import { DateTime } from 'luxon'
import { consumeNavFeedback } from '@/composables/useDashboardNavFeedback'
import { useStore } from 'vuex'
import isEmpty from 'lodash/isEmpty'
import sortBy from 'lodash/sortBy'
import reverse from 'lodash/reverse'
import find from 'lodash/find'
import { getCurrentUkTaxYear } from '@/helpers/ukTaxYear'
import { isDonorAddedDonation } from '@/helpers/taxPackDonations'
import { useDonorFinancialYears } from '@/composables/useDonorFinancialYears'
import { useManualGivingDonations } from '@/composables/useManualGivingDonations'
import type { UserStatement } from '@/types/userStatement'
import type { DonorFinancialYear } from '@/types/donorFinancialYear'
import type { StatementYears, HistoricStatementYear } from '@/types/statementYears'

/**
 * Loads issued statements and donation years for the statements page.
 */
export function useStatementsPage() {
  const store = useStore()
  const currentTaxYear = getCurrentUkTaxYear()

  const {
    sortedYears,
    isReady: donationsReady,
    isLoading: donationsLoading,
    apiError: donationsError,
    loadDonations,
  } = useDonorFinancialYears({ autoLoad: false })

  const {
    manualDonations,
    isLoading: manualDonationsLoading,
    loadManualDonations,
  } = useManualGivingDonations()

  const userStatements = computed(
    () => (store.getters.userStatements as UserStatement[] | null) ?? []
  )

  const statementYears = computed(
    () => store.getters.statementYears as StatementYears | null
  )

  const needsStatementsFetch =
    isEmpty(userStatements.value) || statementYears.value === null

  const statementsReady = ref(!needsStatementsFetch)
  const statementsLoading = ref(needsStatementsFetch)
  const statementsError = ref(false)

  const token = computed(() => store.getters.token as string | null)

  const sortedStatements = computed(() =>
    reverse(sortBy([...userStatements.value], 'period'))
  )

  const latestStatementPeriodKey = `${currentTaxYear.startYear - 1}-${currentTaxYear.startYear}`

  const startYearOf = (periodKey: string) =>
    parseInt(periodKey.split('-')[0], 10)

  const statementByPeriod = computed(() => {
    const map = new Map<string, UserStatement>()
    for (const statement of userStatements.value) {
      map.set(statement.period, statement)
    }
    return map
  })

  /** Tax-year period keys the donor logged manual (donor-added) donations in. */
  const manualDonationPeriodKeys = computed(() => {
    const periods = new Set<string>()
    for (const donation of manualDonations.value) {
      if (!isDonorAddedDonation(donation) || !donation.time) continue
      const date = DateTime.fromISO(donation.time, { zone: 'Europe/London' })
      if (!date.isValid) continue
      periods.add(getCurrentUkTaxYear(date).periodKey)
    }
    return periods
  })

  /** Most recent issued annual statement (e.g. 2025-2026 while in 2026-2027). */
  const latestIssuedStatement = computed(() =>
    find(sortedStatements.value, { period: latestStatementPeriodKey })
  )

  const latestStatementFinancialYear = computed(
    (): DonorFinancialYear | null => {
      const stmt = latestIssuedStatement.value
      if (stmt) {
        const donationsYear = find(sortedYears.value, { period: stmt.period })
        if (donationsYear) return donationsYear
        return {
          period: stmt.period,
          donations: [],
          totalDonations: stmt.totalDonated,
          totalGiftAid: stmt.totalGiftAid,
        }
      }
      // No issued statement for the latest period; still show a zero-value
      // summary when the donor logged manual donations that year.
      if (manualDonationPeriodKeys.value.has(latestStatementPeriodKey)) {
        return {
          period: latestStatementPeriodKey,
          donations: [],
          totalDonations: 0,
          totalGiftAid: 0,
        }
      }
      return null
    }
  )

  /** All statement years the API knows about (full list, not plan-gated). */
  const knownStatementPeriods = computed(() => {
    const periods = new Set<string>(statementYears.value?.years ?? [])
    for (const statement of userStatements.value) periods.add(statement.period)
    return periods
  })

  /**
   * Historic statement rows, newest first. Fills every year between the oldest
   * and newest known statement year with zero-value rows, and adds any older
   * years the donor has manual donations for. Excludes the current tax year and
   * the latest issued statement (shown in their own sections).
   */
  const historicStatementYears = computed<HistoricStatementYear[]>(() => {
    const knownStarts = [...knownStatementPeriods.value]
      .map(startYearOf)
      .filter((year) => !Number.isNaN(year))
    const manualStarts = [...manualDonationPeriodKeys.value]
      .map(startYearOf)
      .filter((year) => !Number.isNaN(year))

    const startYears = new Set<number>()

    // Back-fill every year between the oldest and newest known statement year.
    if (knownStarts.length > 0) {
      const oldestKnown = Math.min(...knownStarts)
      const newestKnown = Math.max(...knownStarts)
      for (let year = oldestKnown; year <= newestKnown; year++) {
        startYears.add(year)
      }
    }

    // Add every year the donor logged manual donations in, including years
    // outside the known statement range on either side. The current tax year
    // and latest issued period are dropped below (shown in their own sections).
    for (const year of manualStarts) startYears.add(year)

    const excluded = new Set([
      currentTaxYear.periodKey,
      latestStatementPeriodKey,
    ])

    return [...startYears]
      .sort((a, b) => b - a)
      .map((startYear): HistoricStatementYear => {
        const period = `${startYear}-${startYear + 1}`
        const issued = statementByPeriod.value.get(period) ?? null
        return {
          period,
          totalDonated: issued?.totalDonated ?? 0,
          totalGiftAid: issued?.totalGiftAid ?? 0,
          issuedStatement: issued,
        }
      })
      .filter((year) => !excluded.has(year.period))
  })

  const hasHistoricYears = computed(
    () => historicStatementYears.value.length > 0
  )

  const financialYearForPeriod = (
    period: string
  ): DonorFinancialYear | undefined => find(sortedYears.value, { period })

  const isReady = computed(() => donationsReady.value && statementsReady.value)

  // Manual donations feed historic/manual-only rows, so keep the page in its
  // loading state until they resolve. On free plans loadManualDonations returns
  // early without ever setting isLoading, so this can't hang non-paying donors.
  const isPageLoading = computed(
    () =>
      donationsLoading.value ||
      statementsLoading.value ||
      manualDonationsLoading.value ||
      !isReady.value
  )

  const apiError = computed(() => donationsError.value || statementsError.value)

  const hasContent = computed(
    () => latestStatementFinancialYear.value || hasHistoricYears.value
  )

  const loadStatements = async () => {
    const haveStatements = !isEmpty(userStatements.value)
    const haveYears = statementYears.value !== null
    if (haveStatements && haveYears) {
      statementsReady.value = true
      statementsLoading.value = false
      return
    }

    statementsLoading.value = true
    try {
      await Promise.all([
        haveStatements
          ? Promise.resolve()
          : store.dispatch('fetchUserStatements'),
        haveYears ? Promise.resolve() : store.dispatch('fetchStatementYears'),
      ])
      statementsReady.value = true
    } catch (error) {
      console.error(error)
      statementsError.value = true
    } finally {
      statementsLoading.value = false
    }
  }

  onMounted(async () => {
    consumeNavFeedback()
    if (token.value === null) {
      await new Promise((r) => setTimeout(r, 2500))
    }
    try {
      await Promise.all([
        loadDonations(),
        loadStatements(),
        // Best-effort: manual donations only enrich the page. loadManualDonations
        // rethrows non-404 errors, so swallow here — a failed manual fetch must
        // not reject the mount and leave the global loader stuck.
        loadManualDonations().catch((error) => {
          console.error('Failed to load manual donations for statements', error)
        }),
      ])
    } finally {
      store.commit('SET_LOADING', false)
    }
  })

  return {
    latestIssuedStatement,
    latestStatementFinancialYear,
    latestStatementPeriodKey,
    historicStatementYears,
    hasHistoricYears,
    sortedStatements,
    financialYearForPeriod,
    isPageLoading,
    apiError,
    hasContent,
  }
}
